import React, { useMemo, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Collapse,
    IconButton,
    Typography,
    TextField,
    InputAdornment,
    Box,
    Chip,
    Stack,
    TableSortLabel,
    Avatar,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    alpha,
    useTheme,
} from '@mui/material';
import {
    KeyboardArrowDown as ExpandMoreIcon,
    KeyboardArrowUp as ExpandLessIcon,
    Search as SearchIcon,
    Add as AddIcon,
    ChangeCircle as ChangeCircleIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { decodeTime } from 'ulid';

/* ------------------------------------------------------------------ */
/* Types and Helpers                                                  */
/* ------------------------------------------------------------------ */

type AuditOperation = 'CREATE' | 'UPDATE' | 'DELETE';
type SortField = 'timestamp' | 'email' | 'operation';
type SortDirection = 'asc' | 'desc';

interface AuditEntry {
    email: string;
    op: string; // Changed from AuditOperation to string to match API
    ts?: string;
    delta: Record<string, { before?: unknown; after?: unknown }>;
}

interface AuditLogTableProps {
    audits: AuditEntry[];
    pageSize?: number;
}

const getOperationColor = (theme: any, op: string): string => {
    switch (op) {
        case 'CREATE':
            return theme.palette.success.main;
        case 'UPDATE':
            return theme.palette.info.main;
        case 'DELETE':
            return theme.palette.error.main;
        default:
            return theme.palette.text.secondary;
    }
};

const getOperationIcon = (op: string): React.ReactElement => {
    switch (op) {
        case 'CREATE':
            return <AddIcon fontSize='small' />;
        case 'UPDATE':
            return <ChangeCircleIcon fontSize='small' />;
        case 'DELETE':
            return <DeleteIcon fontSize='small' />;
        default:
            return <ChangeCircleIcon fontSize='small' />;
    }
};

const getInitials = (email?: string): string => email?.[0]?.toUpperCase() ?? '?';

const formatValue = (value: unknown): string =>
    value === null || value === undefined ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value);

const isEmpty = (value: unknown): boolean => {
    return (
        value === undefined ||
        value === null ||
        (typeof value === 'object' && value !== null && Object.keys(value).length === 0)
    );
};

const getChangedFieldsCount = (delta: Record<string, { before?: unknown; after?: unknown }>): number =>
    Object.entries(delta).filter(([, deltaData]) => {
        const before = deltaData?.before;
        const after = deltaData?.after;
        // Don't count as changed if both are empty
        if (isEmpty(before) && isEmpty(after)) return false;
        // Count as changed if values are different
        return JSON.stringify(before) !== JSON.stringify(after);
    }).length;

const getChangedFieldsList = (delta: Record<string, { before?: unknown; after?: unknown }>): string[] =>
    Object.entries(delta)
        .filter(([, deltaData]) => {
            const before = deltaData?.before;
            const after = deltaData?.after;
            // Don't count as changed if both are empty
            if (isEmpty(before) && isEmpty(after)) return false;
            // Count as changed if values are different
            return JSON.stringify(before) !== JSON.stringify(after);
        })
        .map(([field]) => field);

/* ------------------------------------------------------------------ */
/* Operation Chip Component                                          */
/* ------------------------------------------------------------------ */

interface OperationChipProps {
    operation: string; // Changed from AuditOperation to string
    size?: 'small' | 'medium';
}

const OperationChip: React.FC<OperationChipProps> = ({ operation, size = 'small' }) => {
    const theme = useTheme();
    const color = getOperationColor(theme, operation);

    return (
        <Chip
            size={size}
            icon={getOperationIcon(operation)}
            label={operation.charAt(0) + operation.slice(1).toLowerCase()}
            variant='outlined'
            sx={{
                borderColor: color,
                color: color,
                '& .MuiChip-icon': { color: color },
                fontWeight: 600,
                letterSpacing: 0.25,
                minWidth: '80px',
            }}
        />
    );
};

/* ------------------------------------------------------------------ */
/* Expandable Row Component                                           */
/* ------------------------------------------------------------------ */

interface ExpandableRowProps {
    audit: AuditEntry;
    isExpanded: boolean;
    onToggleExpanded: () => void;
}

const ExpandableRow: React.FC<ExpandableRowProps> = ({ audit, isExpanded, onToggleExpanded }) => {
    const theme = useTheme();
    const timestamp = audit.ts ? dayjs(decodeTime(audit.ts.split('ts#')[1])) : null;
    const changedFieldsCount = getChangedFieldsCount(audit.delta);
    const changedFields = getChangedFieldsList(audit.delta);

    return (
        <>
            <TableRow
                hover
                sx={{
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                }}
                onClick={onToggleExpanded}>
                <TableCell>
                    <IconButton size='small'>{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                </TableCell>

                <TableCell>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>{getInitials(audit.email)}</Avatar>
                        <Typography variant='body2' fontWeight={500}>
                            {audit.email}
                        </Typography>
                    </Stack>
                </TableCell>

                <TableCell>
                    <OperationChip operation={audit.op} />
                </TableCell>

                <TableCell>
                    <Typography variant='body2'>{timestamp ? timestamp.format('MMM D, YYYY') : '—'}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                        {timestamp ? timestamp.format('h:mm A') : ''}
                    </Typography>
                </TableCell>

                <TableCell>
                    <Typography variant='body2' fontWeight={500}>
                        {changedFieldsCount} field{changedFieldsCount !== 1 ? 's' : ''}
                    </Typography>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                        {changedFields.slice(0, 3).join(', ')}
                        {changedFields.length > 3 && ` +${changedFields.length - 3} more`}
                    </Typography>
                </TableCell>
            </TableRow>

            <TableRow>
                <TableCell sx={{ py: 0 }} colSpan={5}>
                    <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                        <Box sx={{ py: 2 }}>
                            <Table size='small'>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', border: 'none' }}>
                                            Field
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: 'error.main', border: 'none' }}>
                                            Before
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: 'success.main', border: 'none' }}>
                                            After
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(audit.delta)
                                        .filter(([, deltaData]) => {
                                            const before = deltaData?.before;
                                            const after = deltaData?.after;
                                            // Only show entries where at least one value is not empty
                                            return !isEmpty(before) || !isEmpty(after);
                                        })
                                        .map(([field, deltaData]) => {
                                            const before = deltaData?.before;
                                            const after = deltaData?.after;
                                            const hasChanged = JSON.stringify(before) !== JSON.stringify(after);
                                            const beforeBg = hasChanged
                                                ? alpha(theme.palette.error.light, 0.1)
                                                : 'transparent';
                                            const afterBg = hasChanged
                                                ? alpha(theme.palette.success.light, 0.1)
                                                : 'transparent';

                                            return (
                                                <TableRow key={field}>
                                                    <TableCell sx={{ fontWeight: 500, border: 'none', py: 1 }}>
                                                        {field}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            bgcolor: beforeBg,
                                                            border: 'none',
                                                            py: 1,
                                                            borderRadius: 1,
                                                        }}>
                                                        <Typography
                                                            variant='body2'
                                                            color='error.main'
                                                            sx={{
                                                                fontFamily: 'monospace',
                                                                fontSize: '0.75rem',
                                                                wordBreak: 'break-all',
                                                            }}>
                                                            {formatValue(before)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            bgcolor: afterBg,
                                                            border: 'none',
                                                            py: 1,
                                                            borderRadius: 1,
                                                        }}>
                                                        <Typography
                                                            variant='body2'
                                                            color='success.main'
                                                            sx={{
                                                                fontFamily: 'monospace',
                                                                fontSize: '0.75rem',
                                                                wordBreak: 'break-all',
                                                            }}>
                                                            {formatValue(after)}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

/* ------------------------------------------------------------------ */
/* Main AuditLogTable Component                                       */
/* ------------------------------------------------------------------ */

export const AuditLogTable: React.FC<AuditLogTableProps> = ({ audits, pageSize = 10 }) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [operationFilter, setOperationFilter] = useState<string>('ALL'); // Changed from AuditOperation | 'ALL' to string
    const [sortField, setSortField] = useState<SortField>('timestamp');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [currentPage, setCurrentPage] = useState<number>(1);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const toggleRowExpanded = (index: number) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(index)) {
            newExpandedRows.delete(index);
        } else {
            newExpandedRows.add(index);
        }
        setExpandedRows(newExpandedRows);
    };

    const filteredAndSortedAudits = useMemo(() => {
        const filtered = audits.filter((audit) => {
            const matchesSearch =
                audit.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                Object.keys(audit.delta).some((field) => field.toLowerCase().includes(searchTerm.toLowerCase()));

            const matchesOperation = operationFilter === 'ALL' || audit.op === operationFilter;

            return matchesSearch && matchesOperation;
        });

        return filtered.sort((a, b) => {
            let aValue: string | number;
            let bValue: string | number;

            switch (sortField) {
                case 'timestamp':
                    aValue = a.ts ? decodeTime(a.ts.split('ts#')[1]) : 0;
                    bValue = b.ts ? decodeTime(b.ts.split('ts#')[1]) : 0;
                    break;
                case 'email':
                    aValue = a.email;
                    bValue = b.email;
                    break;
                case 'operation':
                    aValue = a.op;
                    bValue = b.op;
                    break;
                default:
                    return 0;
            }

            if (sortDirection === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });
    }, [audits, searchTerm, operationFilter, sortField, sortDirection]);

    const paginatedAudits = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredAndSortedAudits.slice(startIndex, startIndex + pageSize);
    }, [filteredAndSortedAudits, currentPage, pageSize]);

    const totalPages = Math.ceil(filteredAndSortedAudits.length / pageSize);

    return (
        <Paper elevation={0} sx={{ width: '100%' }}>
            {/* Search and Filter Controls */}
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction='row' spacing={2} alignItems='center'>
                    <TextField
                        size='small'
                        placeholder='Search by user or field name...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon fontSize='small' />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 300 }}
                    />

                    <FormControl size='small' sx={{ minWidth: 120 }}>
                        <InputLabel>Operation</InputLabel>
                        <Select
                            value={operationFilter}
                            onChange={(e) => setOperationFilter(e.target.value as string)} // Updated type
                            label='Operation'>
                            <MenuItem value='ALL'>All</MenuItem>
                            <MenuItem value='CREATE'>Create</MenuItem>
                            <MenuItem value='UPDATE'>Update</MenuItem>
                            <MenuItem value='DELETE'>Delete</MenuItem>
                        </Select>
                    </FormControl>

                    <Typography variant='body2' color='text.secondary' sx={{ ml: 'auto' }}>
                        {filteredAndSortedAudits.length} entries found
                    </Typography>
                </Stack>
            </Box>

            {/* Table */}
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 48 }}></TableCell>

                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'email'}
                                    direction={sortField === 'email' ? sortDirection : 'desc'}
                                    onClick={() => handleSort('email')}>
                                    User
                                </TableSortLabel>
                            </TableCell>

                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'operation'}
                                    direction={sortField === 'operation' ? sortDirection : 'desc'}
                                    onClick={() => handleSort('operation')}>
                                    Operation
                                </TableSortLabel>
                            </TableCell>

                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'timestamp'}
                                    direction={sortField === 'timestamp' ? sortDirection : 'desc'}
                                    onClick={() => handleSort('timestamp')}>
                                    Date & Time
                                </TableSortLabel>
                            </TableCell>

                            <TableCell>Changes</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginatedAudits.map((audit, index) => {
                            const globalIndex = (currentPage - 1) * pageSize + index;
                            return (
                                <ExpandableRow
                                    key={globalIndex}
                                    audit={audit}
                                    isExpanded={expandedRows.has(globalIndex)}
                                    onToggleExpanded={() => toggleRowExpanded(globalIndex)}
                                />
                            );
                        })}

                        {paginatedAudits.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography color='text.secondary'>
                                        No audit entries found matching your search criteria.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => setCurrentPage(page)}
                        color='primary'
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Paper>
    );
};
